import { useQueryStates } from 'nuqs';
import { parseAsSieveFilters, type SieveFilterOperator } from '~/util/sieve/filter';
import { useDataTableFilterInjectContext } from './DataTableFilterInjectContext';

interface useQueryDataTableFilterProps<T = string> {
	name: string;
	operator?: SieveFilterOperator;
	convertFromQueryParam?: (value: string) => T;
	convertToQueryParam?: (value: T) => string;
}

export function useQueryDataTableFilter<T = string>({
	name,
	operator = '==',
	convertFromQueryParam,
	convertToQueryParam
}: useQueryDataTableFilterProps<T>) {
	const defaultFilters = useDataTableFilterInjectContext();
	const [search, setSearch] = useQueryStates({
		filters: parseAsSieveFilters.withDefault(defaultFilters?.defaultFilters ?? [])
	});
	const preConvertedValue = search.filters.find(f => f.name === name)?.value ?? null;
	const value = preConvertedValue && convertFromQueryParam ? convertFromQueryParam(preConvertedValue.toString()) : preConvertedValue;

	const setValue = (newValue: string | number | boolean | Date | null) => {
		setSearch(prev => {
			const existing = prev.filters.filter(f => f.name !== name);
			if (!newValue && newValue !== false && newValue !== 0) {
				return existing.length > 0 ? { ...prev, filters: existing } : { ...prev, filters: null };
			}
			return {
				...prev,
				filters: [...existing, { name, operator, value: convertToQueryParam ? convertToQueryParam(newValue as T) : newValue }]
			};
		});
	};

	return [value as T, setValue] as const;
}
